import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import { BrushTool } from '~/engine/tool/tools/contouring-tools/BrushTool';
import { brushOptionsState, StrokeMode } from '~/state/atoms/toolsAtoms';
import { cmToMm, formatMmAsCm, mmToCm } from '~/utils/units';
import ToolTrayModeToggle from './ToolTrayModeToggle';
import ToolTraySlider from './ToolTraySlider';

interface BrushToolOptionsProps {
    /** Switches shown after the brush's own, before its diameter. */
    children?: ReactNode;
}

export default function BrushToolOptions({ children }: BrushToolOptionsProps) {
    const intl = useIntl();
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);

    const handleSetStrokeMode = (strokeMode: StrokeMode) => {
        setBrushOptions((options) => ({ ...options, strokeMode }));
    };

    const handleSetBrushSize = (valueCm: number) => {
        const clampedCm = Math.max(0.2, valueCm);
        const newDiameter = cmToMm(clampedCm);
        if (newDiameter > BrushTool.MAX_BRUSH_SIZE) {
            return;
        }
        setBrushOptions((options) => ({ ...options, diameter: newDiameter }));
    };

    return (
        <>
            <ToolTrayModeToggle
                value={brushOptions.strokeMode}
                onChange={handleSetStrokeMode}
                testIdPrefix="brush-mode"
            />
            {brushOptions.erase && (
                <CompactToggle
                    labelId="adaptiveErase"
                    tooltipId="eraseSmartTooltip"
                    value={brushOptions.mode === 'smart'}
                    onChange={(adaptive) =>
                        setBrushOptions((options) => ({
                            ...options,
                            mode: adaptive ? 'smart' : 'static',
                        }))
                    }
                />
            )}
            {children}
            <ToolTraySlider
                label={intl.formatMessage({ id: 'toolOptions.diameter' })}
                value={mmToCm(brushOptions.diameter)}
                onChange={handleSetBrushSize}
                step={0.1}
                min={-0.4}
                max={4.0}
                thumbMarginLeft="-7px"
                valueText={formatMmAsCm(brushOptions.diameter, 1)}
                unit="cm"
            />
        </>
    );
}
